import { lazy, Suspense, type ComponentType } from 'react'
import { Outlet, RouteObject } from 'react-router-dom'

import Layout from '@/App'
import Loading from '@/components/loading'
import RouteErrorBoundary from '@/components/RouteErrorBoundary'

import { RouterBeforeEach } from './permission'
export type RouterMap = RouteObject & {
  meta?: any
  children?: RouterMap[]
  exact?: boolean
  index?: boolean
  parentPath?: string
}
export interface childrenRoutesType {
  index?: boolean
  exact?: boolean
  element?: React.ReactNode
  path?: string
  isParent?: boolean
  parentPath?: string
  meta?: {
    permission?: boolean
  }
  InheritedPermissions?: boolean
  children?: childrenRoutesType[]
}

const legacyComponents = import.meta.glob('../views/**/index.tsx')
const pageComponents = import.meta.glob('../views/**/page/index.tsx')

type ModuleLoader = () => Promise<unknown>

const insensitiveLegacyFiles: Record<string, ModuleLoader> = Object.keys(legacyComponents).reduce(
  (acc, key) => {
    acc[key.toLowerCase()] = legacyComponents[key]
    return acc
  },
  {} as Record<string, ModuleLoader>,
)

const insensitivePageFiles: Record<string, ModuleLoader> = Object.keys(pageComponents).reduce(
  (acc, key) => {
    acc[key.toLowerCase()] = pageComponents[key]
    return acc
  },
  {} as Record<string, ModuleLoader>,
)

const lazyRetryStorageKey = (componentPath: string) => `lazy-retry:${componentPath}`

function lazyWithRetry<T extends ComponentType<unknown>>(
  componentPath: string,
  loader: ModuleLoader,
) {
  return lazy(async () => {
    try {
      const mod = (await loader()) as { default: T }
      sessionStorage.removeItem(lazyRetryStorageKey(componentPath))
      return mod
    } catch (error) {
      console.error(`[router] failed to load ${componentPath}`, error)
      const retryKey = lazyRetryStorageKey(componentPath)
      if (!sessionStorage.getItem(retryKey)) {
        sessionStorage.setItem(retryKey, '1')
        window.location.reload()
      }
      throw error
    }
  })
}

export const lazyLoad = (componentPath: string, meta: any) => {
  const pageLoader = insensitivePageFiles[`../views/${componentPath}/page/index.tsx`.toLowerCase()]
  const legacyLoader = insensitiveLegacyFiles[`../views/${componentPath}/index.tsx`.toLowerCase()]
  const loader = pageLoader || legacyLoader

  if (!loader) {
    console.error(`[router] lazy component not found: ${componentPath}`)
    return (
      <RouterBeforeEach meta={meta}>
        <RouteErrorBoundary componentPath={componentPath}>
          <Suspense fallback={<Loading />}>
            <div>Page not found: {componentPath}</div>
          </Suspense>
        </RouteErrorBoundary>
      </RouterBeforeEach>
    )
  }

  const LazyComponent = lazyWithRetry(componentPath, loader)

  return (
    <RouterBeforeEach meta={meta}>
      <RouteErrorBoundary componentPath={componentPath}>
        <Suspense fallback={<Loading />}>
          <LazyComponent />
        </Suspense>
      </RouteErrorBoundary>
    </RouterBeforeEach>
  )
}

export const generateElement = (routes: childrenRoutesType[]) => {
  if (routes.length === 0) return []
  return routes.map((item: childrenRoutesType): childrenRoutesType => {
    const { path, parentPath, meta, children, index } = item as childrenRoutesType

    if (path === '/') return { path: '/', element: <Layout />, children: generateElement(item?.children || []) }

    if (index || path === '*') return item as RouterMap

    const componentPath = parentPath ? parentPath + '/' + path : path

    if (children) {
      const newChildren = children.map((iten: childrenRoutesType): childrenRoutesType => {
        iten.parentPath = componentPath
        return iten
      })
      newChildren.unshift({
        index: true,
        element: componentPath ? lazyLoad(componentPath, meta) : <></>,
      })
      return {
        ...item,
        element: <Outlet />,
        children: generateElement(newChildren),
      }
    } else {
      return {
        ...item,
        element: componentPath ? lazyLoad(componentPath, meta) : <></>,
      }
    }
  })
}
